import React, { useState } from 'react';
import { useCreateCourse } from '../api/coursesApi';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/features/auth/components/ErrorAlert';

interface AddCourseFormProps {
  onSuccess?: () => void;
}

const AddCourseForm: React.FC<AddCourseFormProps> = ({ onSuccess }) => {
  const [name, setName] = useState('');
  const [lectureCount, setLectureCount] = useState<number>(1);
  const [watchedLectures, setWatchedLectures] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  
  const createCourseMutation = useCreateCourse();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter a course name');
      return;
    }

    if (lectureCount < 1) {
      setError('Lecture count must be at least 1');
      return;
    }

    if (watchedLectures < 0) {
      setError('Watched lectures cannot be negative');
      return;
    }

    if (watchedLectures > lectureCount) {
      setError('Watched lectures cannot exceed total lectures');
      return;
    }

    try {
      await createCourseMutation.mutateAsync({ 
        name: name.trim(), 
        lectureCount,
        watchedLectures
      });
      setName(''); // Clear the form on success
      setLectureCount(1);
      setWatchedLectures(0);
      onSuccess?.(); // Call success callback (e.g., close modal)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="course-name" className="block text-sm font-medium text-gray-700 mb-2">
            Course Name
          </label>
          <input
            type="text"
            id="course-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter course name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={createCourseMutation.isPending}
          />
        </div>

        <div>
          <label htmlFor="lecture-count" className="block text-sm font-medium text-gray-700 mb-2">
            Total Lectures
          </label>
          <input
            type="number"
            id="lecture-count"
            value={lectureCount}
            onChange={(e) => setLectureCount(parseInt(e.target.value, 10) || 1)}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={createCourseMutation.isPending}
          />
          <p className="mt-1 text-sm text-gray-500">
            How many lectures does this course have?
          </p>
        </div>

        <div>
          <label htmlFor="watched-lectures" className="block text-sm font-medium text-gray-700 mb-2">
            Lectures Already Watched
          </label>
          <input
            type="number"
            id="watched-lectures"
            value={watchedLectures}
            onChange={(e) => setWatchedLectures(parseInt(e.target.value, 10) || 0)}
            min="0"
            max={lectureCount}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={createCourseMutation.isPending}
          />
          <p className="mt-1 text-sm text-gray-500">
            If you've already watched some lectures, enter the number here (default: 0)
          </p>
        </div>

        <ErrorAlert message={error ?? ''} />

        {createCourseMutation.isSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">Course added successfully!</p>
          </div>
        )}

        <Button type="submit" variant="primary" disabled={createCourseMutation.isPending || !name.trim() || lectureCount < 1 || watchedLectures < 0 || watchedLectures > lectureCount} className="w-full">
          {createCourseMutation.isPending ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Adding Course...
            </span>
          ) : (
            'Add Course'
          )}
        </Button>
      </form>
      
      <div className="mt-4 text-sm text-gray-500">
        Enter your course details to start tracking your progress
      </div>
    </div>
  );
};

export default AddCourseForm; 